import React from 'react';
import { Page, GraduationCap } from 'iconoir-react';
import OrbitingCircles from '../ui/OrbitingCircles';
import TechLogo from '../ui/TechLogo';
import AnimatedElement, { StaggerContainer, StaggerItem } from '../ui/AnimatedElement';

const Skills: React.FC = () => {
  // Detect if mobile for responsive orbit sizing
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Responsive orbit radii
  const innerRadius = isMobile ? 55 : 80;
  const middleRadius = isMobile ? 90 : 130;
  const outerRadius = isMobile ? 125 : 175;
  const iconSizeInner = isMobile ? 32 : 44;
  const iconSizeMiddle = isMobile ? 28 : 40;
  const iconSizeOuter = isMobile ? 26 : 38;

  return (
    <div className="w-full max-w-6xl mx-auto flex flex-col justify-center pt-8 px-4 pb-4 md:p-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-12 items-center">
        {/* ORBITING CIRCLES SECTION */}
        <AnimatedElement variant="scaleIn" className="relative h-[280px] sm:h-[380px] md:h-[480px] w-full flex items-center justify-center">
          {/* Background circles */}
          <div className="absolute w-[200px] h-[200px] sm:w-[300px] sm:h-[300px] md:w-[400px] md:h-[400px] bg-slate-50 rounded-full border border-slate-200/50" />
          <div className="absolute w-[140px] h-[140px] sm:w-[210px] sm:h-[210px] md:w-[280px] md:h-[280px] bg-white rounded-full border border-slate-100" />

          {/* Central Profile - Logo */}
          <div className="z-10 bg-white p-2 sm:p-4 rounded-full shadow-xl border border-slate-100">
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Anthony Abid"
              className="w-10 h-10 sm:w-16 sm:h-16 md:w-24 md:h-24 object-contain"
            />
          </div>

          {/* Inner Orbit - Core Languages */}
          <OrbitingCircles radius={innerRadius} duration={25} iconSize={iconSizeInner} speed={1}>
            <TechLogo name="react" size={isMobile ? 16 : 24} />
            <TechLogo name="typescript" size={isMobile ? 16 : 24} />
            <TechLogo name="python" size={isMobile ? 16 : 24} />
            <TechLogo name="java" size={isMobile ? 16 : 24} />
            <TechLogo name="javascript" size={isMobile ? 16 : 24} />
          </OrbitingCircles>

          {/* Middle Orbit - Tools & Databases */}
          <OrbitingCircles radius={middleRadius} duration={35} reverse iconSize={iconSizeMiddle} speed={0.9}>
            <TechLogo name="postgresql" size={isMobile ? 14 : 22} />
            <TechLogo name="mysql" size={isMobile ? 14 : 22} />
            <TechLogo name="git" size={isMobile ? 14 : 22} />
            <TechLogo name="docker" size={isMobile ? 14 : 22} />
            <TechLogo name="linux" size={isMobile ? 14 : 22} />
            <TechLogo name="nodejs" size={isMobile ? 14 : 22} />
          </OrbitingCircles>

          {/* Outer Orbit - Infrastructure */}
          <OrbitingCircles radius={outerRadius} duration={45} iconSize={iconSizeOuter} speed={0.8}>
            <TechLogo name="aws" size={isMobile ? 12 : 20} />
            <TechLogo name="github" size={isMobile ? 12 : 20} />
            <TechLogo name="vscode" size={isMobile ? 12 : 20} />
            <TechLogo name="tailwindcss" size={isMobile ? 12 : 20} />
            <TechLogo name="bash" size={isMobile ? 12 : 20} />
          </OrbitingCircles>
        </AnimatedElement>

        {/* Text Content */}
        <div className="space-y-4 sm:space-y-6">
          <AnimatedElement variant="fadeUp">
            <span className="text-neutral-500 text-xs sm:text-sm font-mono tracking-wide">
              06 — SKILLS
            </span>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-slate-900 mt-2 sm:mt-3 mb-3 sm:mb-6 tracking-tight">Tech Stack</h2>
            <p className="text-sm sm:text-lg text-slate-600 leading-relaxed">
              My technical foundation is built on modern, scalable technologies. I specialize in full-stack development with a strong emphasis on cloud infrastructure and automation.
            </p>
          </AnimatedElement>

          <StaggerContainer className="grid grid-cols-2 gap-2 sm:gap-4">
            <StaggerItem>
              <div className="bg-white/95 backdrop-blur-md rounded-xl sm:rounded-2xl border border-slate-200 shadow-lg p-3 sm:p-5 hover:shadow-xl hover:border-slate-300 transition-all duration-300">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <GraduationCap className="text-matcha-600" width={16} height={16} />
                  <h3 className="font-bold text-slate-900 text-[10px] sm:text-sm uppercase tracking-wider">Education</h3>
                </div>
                <div className="text-xs sm:text-sm font-bold text-matcha-600">Western Governors University</div>
                <div className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">B.S. Computer Science</div>
                <span className="px-1.5 sm:px-2 py-0.5 rounded text-[8px] sm:text-[10px] font-bold tracking-wide uppercase bg-matcha-50 text-matcha-700 border border-matcha-200">
                  Senior Year
                </span>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="bg-white/95 backdrop-blur-md rounded-xl sm:rounded-2xl border border-slate-200 shadow-lg p-3 sm:p-5 hover:shadow-xl hover:border-slate-300 transition-all duration-300">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <Page className="text-matcha-600" width={16} height={16} />
                  <h3 className="font-bold text-slate-900 text-[10px] sm:text-sm uppercase tracking-wider">Certifications</h3>
                </div>
                <ul className="space-y-1 sm:space-y-2">
                  <li className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-700 font-medium">
                    <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-matcha-500 rounded-full" />
                    ITIL Foundation
                  </li>
                  <li className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-700 font-medium">
                    <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-matcha-500 rounded-full" />
                    Linux Essentials
                  </li>
                </ul>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </div>
    </div>
  );
};

export default Skills;
